import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadSimple, DownloadSimple, Trash, Plus, Users, LockKey,
  MagnifyingGlass, Crown, Copy, ArrowLeft, X, FolderSimple,
  FolderSimplePlus, ArrowsOutSimple, ActivityIcon, CheckSquare,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { downloadFilesAsZip } from "@/lib/zip";
import { useAuth } from "@/context/AuthContext";
import { fmtBytes, fmtRelDate } from "@/components/team/util.jsx";
import { Breadcrumb, Modal, MenuItem } from "@/components/team/widgets.jsx";
import ListView from "@/components/team/ListView.jsx";
import GridView from "@/components/team/GridView.jsx";
import { NewFolderForm } from "@/components/team/NewFolderForm.jsx";
import { RenameForm } from "@/components/team/RenameForm.jsx";
import { MovePicker } from "@/components/team/MovePicker.jsx";
import { InviteForm, ResetForm } from "@/components/team/InviteResetForms.jsx";
import { ShareForm } from "@/components/team/ShareForm.jsx";

export default function TeamDashboard() {
  const { team_id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [team, setTeam] = useState(null);
  const [contents, setContents] = useState({ items: [], folders: [], breadcrumb: [], current_folder_id: null });
  const [storage, setStorage] = useState({ used: 0, quota: 0 });
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState("list");
  const [tab, setTab] = useState("drive");
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState({ pct: 0, label: "" });

  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showRename, setShowRename] = useState(null);

  const isLeader = team && user && team.leader_user_id === user.user_id;

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=/teams/${team_id}`); return; }
    refreshTeam();
     
  }, [user, authLoading, team_id]);

  const refreshTeam = async () => {
    try {
      const { data } = await api.get(`/teams/${team_id}`);
      setTeam(data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 403 || e?.response?.status === 404) navigate("/teams");
    }
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${team_id}/files`, {
        params: { q: search || undefined, sort, folder_id: currentFolderId || undefined },
      });
      setContents(data);
      setStorage({ used: data.storage_used, quota: data.storage_quota });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [team_id, search, sort, currentFolderId]);

  useEffect(() => { if (team) refresh();  }, [team, refresh]);
  useEffect(() => { setSelectedFiles(new Set()); setSelectedFolders(new Set()); }, [currentFolderId]);

  const goToFolder = (folderId) => { setCurrentFolderId(folderId); };

  const onDrop = useCallback(async (accepted) => {
    if (!accepted?.length) return;
    setBusy(true);
    const fd = new FormData();
    accepted.forEach((f) => fd.append("files", f));
    if (currentFolderId) fd.append("folder_id", currentFolderId);
    try {
      await api.post(`/teams/${team_id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${accepted.length} file${accepted.length > 1 ? "s" : ""}`);
      refresh(); refreshTeam();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }, [team_id, currentFolderId, refresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true, maxSize: 100 * 1024 * 1024, noClick: true });

  const downloadOne = async (f) => {
    try {
      const resp = await api.get(`/teams/${team_id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = f.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadSelected = async () => {
    if (!selectedFiles.size) return toast.error("Select files first");
    const picked = contents.items.filter((f) => selectedFiles.has(f.id));
    if (picked.length === 1) return downloadOne(picked[0]);
    setDl({ pct: 0, label: `Preparing ${picked.length} files…` });
    const items = picked.map((f) => ({
      url: `/teams/${team_id}/files/${f.id}/download`,
      filename: f.filename, size: f.size,
    }));
    const res = await downloadFilesAsZip(items, `${team?.name || "team"}-files`, (pct, label) => setDl({ pct, label }));
    setDl({ pct: 0, label: "" });
    if (res.ok) toast.success(`Zipped ${picked.length} files`);
    else toast.error("Download failed");
  };

  const deleteSelected = async () => {
    if (!selectedFiles.size && !selectedFolders.size) return toast.error("Nothing selected");
    const n = selectedFiles.size + selectedFolders.size;
    if (!window.confirm(`Delete ${n} item${n > 1 ? "s" : ""}? Folders delete recursively.`)) return;
    try {
      await Promise.all([
        ...[...selectedFiles].map((id) => api.delete(`/teams/${team_id}/files/${id}`)),
        ...[...selectedFolders].map((id) => api.delete(`/teams/${team_id}/folders/${id}`)),
      ]);
      toast.success("Deleted");
      setSelectedFiles(new Set()); setSelectedFolders(new Set());
      refresh(); refreshTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveSelected = async (targetFolderId) => {
    try {
      await api.post(`/teams/${team_id}/move`, {
        file_ids: [...selectedFiles],
        folder_ids: [...selectedFolders],
        target_folder_id: targetFolderId,
      });
      toast.success("Moved");
      setShowMove(false);
      setSelectedFiles(new Set()); setSelectedFolders(new Set());
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMember = async (mid) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/teams/${team_id}/members/${mid}`); refreshTeam(); toast.success("Member removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const loadActivity = async () => {
    try { const { data } = await api.get(`/teams/${team_id}/activity`); setActivity(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (tab === "activity") loadActivity();  }, [tab]);

  const onFileAction = (action, f) => {
    if (action === "download") downloadOne(f);
    else if (action === "rename") setShowRename({ type: "file", id: f.id, name: f.filename });
    else if (action === "share") setShowShare(f);
    else if (action === "move") { setSelectedFiles(new Set([f.id])); setShowMove(true); }
    else if (action === "delete") {
      if (window.confirm(`Delete ${f.filename}?`)) {
        api.delete(`/teams/${team_id}/files/${f.id}`).then(() => { toast.success("Deleted"); refresh(); refreshTeam(); }).catch((e) => toast.error(formatApiError(e)));
      }
    }
  };

  const onFolderAction = (action, d) => {
    if (action === "rename") setShowRename({ type: "folder", id: d.id, name: d.name });
    else if (action === "move") { setSelectedFolders(new Set([d.id])); setShowMove(true); }
    else if (action === "delete") {
      if (window.confirm(`Delete folder "${d.name}" and ALL its contents?`)) {
        api.delete(`/teams/${team_id}/folders/${d.id}`).then(() => { toast.success("Deleted"); refresh(); refreshTeam(); }).catch((e) => toast.error(formatApiError(e)));
      }
    }
  };

  const selectedCount = selectedFiles.size + selectedFolders.size;
  const filtersAreSearching = !!search;

  if (authLoading || !team) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-black/55">Loading…</div>;
  }
  const pct = Math.min(100, Math.round((storage.used / storage.quota) * 100));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 md:py-12 fade-up">
      <Header team={team} storage={storage} pct={pct} navigate={navigate}/>

      <Tabs tab={tab} setTab={setTab} isLeader={isLeader}/>

      {tab === "drive" && (
        <div data-testid="drive-panel">
          <Toolbar
            newMenuOpen={newMenuOpen} setNewMenuOpen={setNewMenuOpen}
            onNewFolder={() => { setShowNewFolder(true); setNewMenuOpen(false); }}
            onUpload={(files) => { onDrop(files); setNewMenuOpen(false); }}
            search={search} setSearch={setSearch}
            sort={sort} setSort={setSort}
            view={view} setView={setView}
          />
          <Breadcrumb breadcrumb={contents.breadcrumb} onHome={() => goToFolder(null)} onNav={goToFolder}/>

          {selectedCount > 0 && (
            <SelectionBar
              selectedCount={selectedCount}
              filesCount={selectedFiles.size}
              onDownload={downloadSelected}
              onMove={() => setShowMove(true)}
              onDelete={deleteSelected}
              onClear={() => { setSelectedFiles(new Set()); setSelectedFolders(new Set()); }}
            />
          )}

          {dl.label && (
            <div className="mb-3 p-3 border border-black/15 bg-black/[0.03] rounded-md" data-testid="dl-progress">
              <div className="flex items-center justify-between text-xs font-mono mb-1.5">
                <span className="truncate text-black/70">{dl.label}</span>
                <span className="text-[#10B981]">{dl.pct}%</span>
              </div>
              <div className="h-1.5 bg-black/10 rounded"><div className="h-1.5 bg-[#10B981] rounded transition-all" style={{ width: `${dl.pct}%` }}/></div>
            </div>
          )}

          <div {...getRootProps()} className={`relative rounded-md ${isDragActive ? "ring-2 ring-[#FF3366] ring-offset-2" : ""}`}>
            <input {...getInputProps()}/>
            {isDragActive && (
              <div className="absolute inset-0 z-10 bg-[#FF3366]/5 border-2 border-dashed border-[#FF3366] rounded-md grid place-items-center pointer-events-none">
                <div className="text-center"><UploadSimple size={32} weight="duotone" className="mx-auto mb-2 text-[#FF3366]"/><div className="font-bold">Drop to upload here</div></div>
              </div>
            )}

            {contents.folders.length === 0 && contents.items.length === 0 ? (
              <EmptyState searching={filtersAreSearching}/>
            ) : view === "list" ? (
              <ListView
                contents={contents}
                selectedFiles={selectedFiles}
                selectedFolders={selectedFolders}
                onToggleFile={(id) => setSelectedFiles((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                onToggleFolder={(id) => setSelectedFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                onOpenFolder={goToFolder}
                onFileAction={onFileAction}
                onFolderAction={onFolderAction}
              />
            ) : (
              <GridView
                contents={contents}
                selectedFiles={selectedFiles}
                selectedFolders={selectedFolders}
                onToggleFile={(id) => setSelectedFiles((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                onToggleFolder={(id) => setSelectedFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                onOpenFolder={goToFolder}
                onDownload={downloadOne}
              />
            )}
          </div>

          <StorageFooter used={storage.used} quota={storage.quota} pct={pct}/>
        </div>
      )}

      {tab === "members" && (
        <MembersPanel team={team} isLeader={isLeader} onAdd={() => setShowInvite(true)} onRemove={removeMember}/>
      )}
      {tab === "activity" && (
        <ActivityPanel activity={activity}/>
      )}
      {tab === "settings" && isLeader && (
        <SettingsPanel onReset={() => setShowReset(true)}/>
      )}

      {showNewFolder && <Modal title="New folder" onClose={() => setShowNewFolder(false)}><NewFolderForm teamId={team_id} parent={currentFolderId} onDone={() => { setShowNewFolder(false); refresh(); }}/></Modal>}
      {showRename && <Modal title={`Rename ${showRename.type}`} onClose={() => setShowRename(null)}><RenameForm teamId={team_id} item={showRename} onDone={() => { setShowRename(null); refresh(); }}/></Modal>}
      {showMove && <Modal title="Move to" onClose={() => setShowMove(false)}><MovePicker teamId={team_id} onPick={moveSelected}/></Modal>}
      {showInvite && isLeader && <Modal title="Add member" onClose={() => setShowInvite(false)}><InviteForm teamId={team_id} onDone={() => { setShowInvite(false); refreshTeam(); }}/></Modal>}
      {showReset && isLeader && <Modal title="Reset team password" onClose={() => setShowReset(false)}><ResetForm teamId={team_id} onDone={() => setShowReset(false)}/></Modal>}
      {showShare && <Modal title={`Share "${showShare.filename}"`} onClose={() => setShowShare(null)}><ShareForm teamId={team_id} file={showShare} onDone={() => setShowShare(null)}/></Modal>}

      {busy && <div className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 text-sm rounded-md shadow-lg">Uploading…</div>}
    </div>
  );
}

function Header({ team, storage, pct, navigate }) {
  const copy = () => { navigator.clipboard.writeText(team.team_id); toast.success("Team ID copied"); };
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <button onClick={() => navigate("/teams")} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-black/55 hover:text-[#FF3366] mb-2">
          <ArrowLeft size={14}/> All teams
        </button>
        <h1 className="text-3xl md:text-4xl tracking-tighter leading-none flex items-center gap-3">
          {team.name}
          {team.status === "suspended" && <span className="text-xs font-mono uppercase bg-[#FF3366] text-white px-2 py-1">Suspended</span>}
        </h1>
        <div className="text-xs font-mono text-black/55 mt-2 flex items-center gap-3 flex-wrap">
          <span>ID <span className="text-black">{team.team_id}</span>
            <button onClick={copy} data-testid="copy-team-id" className="ml-1.5 text-black/40 hover:text-[#FF3366] align-middle"><Copy size={11} className="inline"/></button>
          </span>
          <span>· {team.member_count}/10 member{team.member_count > 1 ? "s" : ""}</span>
          <span>· {fmtBytes(storage.used)} / {fmtBytes(storage.quota)} ({pct}%)</span>
        </div>
      </div>
    </div>
  );
}

function Tabs({ tab, setTab, isLeader }) {
  const items = [["drive", "Drive"], ["members", "Members"], ["activity", "Activity"]];
  if (isLeader) items.push(["settings", "Settings"]);
  return (
    <div className="flex gap-1 border-b border-black/15 mb-5">
      {items.map(([k, l]) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-black text-black font-medium" : "border-transparent text-black/55 hover:text-black"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function Toolbar({ newMenuOpen, setNewMenuOpen, onNewFolder, onUpload, search, setSearch, sort, setSort, view, setView }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative">
        <button onClick={() => setNewMenuOpen((v) => !v)} data-testid="new-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 rounded-md hover:bg-black/5 hover:border-black/30 shadow-sm">
          <Plus size={16} weight="bold"/> <span className="font-medium text-sm">New</span>
        </button>
        {newMenuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setNewMenuOpen(false)}/>
            <div className="absolute left-0 top-full mt-1 bg-white border border-black/15 rounded-md shadow-lg py-1 z-20 w-52" data-testid="new-menu">
              <MenuItem onClick={onNewFolder} testid="menu-new-folder" icon={<FolderSimplePlus size={16} className="text-[#FFA000]" weight="duotone"/>} label="New folder"/>
              <div className="my-1 border-t border-black/10"/>
              <label className="px-3 py-2 hover:bg-black/5 cursor-pointer flex items-center gap-3 text-sm" data-testid="menu-upload">
                <UploadSimple size={16} className="text-[#10B981]" weight="duotone"/> <span>Upload files</span>
                <input type="file" multiple hidden onChange={(e) => onUpload(Array.from(e.target.files || []))}/>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 border border-black/15 px-3 py-2 bg-white rounded-md flex-1 max-w-md">
        <MagnifyingGlass size={14} className="text-black/55"/>
        <input data-testid="files-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search in this team" className="bg-transparent outline-none text-sm w-full"/>
      </div>

      <select data-testid="files-sort" value={sort} onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2 text-sm border border-black/15 rounded-md bg-white">
        <option value="newest">Newest</option>
        <option value="oldest">Oldest</option>
        <option value="largest">Largest</option>
        <option value="name">Name A→Z</option>
      </select>

      <div className="ml-auto flex gap-1">
        <button onClick={() => setView("list")} className={`p-2 border border-black/15 rounded-md ${view === "list" ? "bg-black text-white border-black" : "bg-white"}`} data-testid="view-list">≡</button>
        <button onClick={() => setView("grid")} className={`p-2 border border-black/15 rounded-md ${view === "grid" ? "bg-black text-white border-black" : "bg-white"}`} data-testid="view-grid">▦</button>
      </div>
    </div>
  );
}

function SelectionBar({ selectedCount, filesCount, onDownload, onMove, onDelete, onClear }) {
  return (
    <div className="mb-3 px-4 py-2 bg-black text-white rounded-md flex items-center justify-between gap-2" data-testid="selection-bar">
      <div className="text-sm"><CheckSquare size={14} weight="fill" className="inline mr-2"/>{selectedCount} selected</div>
      <div className="flex gap-1">
        {filesCount > 0 && (
          <button onClick={onDownload} data-testid="download-selected" className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 hover:bg-white hover:text-black">
            <DownloadSimple size={13} className="inline mr-1"/> {filesCount > 1 ? "Download zip" : "Download"}
          </button>
        )}
        <button onClick={onMove} data-testid="move-selected" className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 hover:bg-white hover:text-black">
          <ArrowsOutSimple size={13} className="inline mr-1"/> Move
        </button>
        <button onClick={onDelete} data-testid="delete-selected" className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 hover:bg-[#FF3366] hover:border-[#FF3366]">
          <Trash size={13} className="inline mr-1"/> Delete
        </button>
        <button onClick={onClear} className="px-3 py-1.5 text-xs uppercase tracking-widest border border-white/40 hover:bg-white hover:text-black"><X size={13}/></button>
      </div>
    </div>
  );
}

function EmptyState({ searching }) {
  return (
    <div className="border border-dashed border-black/15 p-16 text-center rounded-md" data-testid="drive-empty">
      <FolderSimple size={40} weight="duotone" className="mx-auto mb-3 text-[#FFA000]"/>
      <div className="text-base font-medium mb-1">{searching ? "No results" : "This folder is empty"}</div>
      <div className="text-sm text-black/55 mb-4">{searching ? "Try a different search" : "Drag files anywhere or use the New button"}</div>
    </div>
  );
}

function StorageFooter({ used, quota, pct }) {
  return (
    <div className="mt-6">
      <div className="text-xs font-mono text-black/55 flex items-center justify-between mb-1">
        <span>{fmtBytes(used)} of {fmtBytes(quota)} used</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-black/10 rounded"><div className="h-1.5 bg-[#10B981] rounded transition-all" style={{ width: `${pct}%` }}/></div>
    </div>
  );
}

function MembersPanel({ team, isLeader, onAdd, onRemove }) {
  return (
    <div className="brut-card p-6 rounded-md" data-testid="members-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm flex items-center gap-2"><Users size={14}/> {team.members.length} of 10 members</div>
        {isLeader && <button onClick={onAdd} className="px-3 py-1.5 bg-black text-white text-xs uppercase tracking-widest rounded" data-testid="invite-btn">+ Add member</button>}
      </div>
      <ul className="divide-y divide-black/10">
        {team.members.map((m) => (
          <li key={m.user_id} className="py-3 flex items-center justify-between gap-3" data-testid={`member-${m.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-black text-white grid place-items-center font-mono rounded-full">{(m.name || m.email || "?")[0].toUpperCase()}</div>
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-2">{m.name || m.email}{m.role === "leader" && <Crown size={14} className="text-[#F97316]" weight="fill"/>}</div>
                <div className="text-xs font-mono text-black/55 truncate">{m.email || m.mobile || "—"}</div>
              </div>
            </div>
            {isLeader && m.role !== "leader" && (
              <button onClick={() => onRemove(m.user_id)} className="text-xs uppercase tracking-widest text-black/55 hover:text-[#FF3366]" data-testid={`remove-${m.user_id}`}>Remove</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityPanel({ activity }) {
  return (
    <div className="brut-card p-6 rounded-md" data-testid="activity-panel">
      <div className="text-sm mb-4 flex items-center gap-2"><ActivityIcon size={14}/> Recent activity</div>
      {activity.length === 0 ? <div className="text-black/55 text-sm">No activity yet.</div> : (
        <ul className="divide-y divide-black/10">
          {activity.map((a) => (
            <li key={a.id} className="py-2.5 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm"><span className="font-mono text-[#FF3366]">{a.action}</span></div>
                {a.meta && Object.keys(a.meta).length > 0 && <div className="text-[11px] font-mono text-black/55">{JSON.stringify(a.meta)}</div>}
              </div>
              <div className="text-[11px] font-mono text-black/55 whitespace-nowrap">{fmtRelDate(a.created_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsPanel({ onReset }) {
  return (
    <div className="brut-card p-6 rounded-md space-y-4" data-testid="settings-panel">
      <div className="text-sm flex items-center gap-1"><LockKey size={12}/> Team password</div>
      <p className="text-sm text-black/60">Share the new password with members. Existing sessions remain valid.</p>
      <button onClick={onReset} className="px-4 py-2 bg-black text-white text-sm rounded" data-testid="reset-password-btn">Reset password</button>
    </div>
  );
}
